import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, takeUntil, Subject, RetryConfig, retry, timer } from 'rxjs';

import { STORAGE_UNIT_SIZE } from './app.constants';
import { LoggingService } from './logging.service';
import { NotificationService } from './notification.service';
import { XferBlob, XferBlobSession } from './shared/models/xfer.model';
import { OperationFeedbackItemDownloadStatus } from './xfer/components/operation-feedback/operation-feedback.enums';
import {
  DownloadPayload,
  OperationFeedbackItemData,
} from './xfer/components/operation-feedback/operation-feedback.interfaces';

const SESSION_CHECK_DELAY_MS = 5000;
const PARALLEL_DOWNLOADS = 10;
const CHUNK_SIZE = 10 * STORAGE_UNIT_SIZE * STORAGE_UNIT_SIZE;
const MAX_CONCURRENT_REQUESTS = 20;
const RETRY_COUNT = 5;
const RETRY_INITIAL_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 10000;

// core-api does not support range-read headers for fs:// (dev only)
const rangeReadUnsupported = RegExp('^/api/files/.*/download');
const allowedUrls = [
  RegExp('^/xfer/blob-sessions'),
  RegExp('^/xfer/blobs'),
  RegExp('^/xfer/exports'),
  RegExp('^/api/files'),
  RegExp('^https://storage\\.googleapis\\.com'),
  RegExp('^https://.*\\.amazonaws\\.com'),
  RegExp('^https://.*\\.blob\\.core\\.windows\\.net'),
];


const _window = window as any; // eslint-disable-line @typescript-eslint/no-explicit-any

interface PageQueryParams {
  filter: string[]
  sort: string
  limit: number
  after_id?: string
}

class Throttler {
  private activeCount = 0;
  private queue: Array<() => void> = [];

  constructor(private limit: number) { }

  async run<T>(task: () => Promise<T>): Promise<T> {
    if (this.activeCount >= this.limit) {
      await new Promise<void>((resolve) => this.queue.push(resolve));
    }

    this.activeCount++;
    try {
      return await task();
    } finally {
      this.activeCount--;
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next?.();
      }
    }
  }
}

function exponentialBackoffRetry(): RetryConfig {
  return {
    count: RETRY_COUNT,
    delay: (_, retryCount) => {
      const delay = Math.min(
        RETRY_INITIAL_DELAY_MS * Math.pow(2, retryCount - 1),
        RETRY_MAX_DELAY_MS,
      );
      return timer(delay);
    },
  };
}

async function fetchWithRetry(
  url: string,
  options?: RequestInit,
  retries = RETRY_COUNT,
): Promise<Response> {
  let lastError: Error;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (allowedUrls.find(regex => regex.test(url)) !== undefined) {
        const response = await fetch(url, options);
        if (!response.ok) {
          throw Object.assign(new Error(`HTTP error! status: ${response.status}`), {
            status: response.status,
          });
        }
        return response;
      } else {
        throw new Error(`URL not allowed: ${url}`);
      }
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        const delay = Math.min(
          RETRY_INITIAL_DELAY_MS * Math.pow(2, attempt),
          RETRY_MAX_DELAY_MS,
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
}

@Injectable({
  providedIn: 'root',
})
export class DownloadService implements OnDestroy {
  public downloads$ = new BehaviorSubject<OperationFeedbackItemData[]>([]);
  private downloads: OperationFeedbackItemData[] = [];
  private throttler = new Throttler(MAX_CONCURRENT_REQUESTS);
  private activeDownloads = new Map<string, AbortController>();

  constructor(
    private logger: LoggingService,
    private http: HttpClient,
    private notificationService: NotificationService,
  ) { }

  ngOnDestroy(): void {
    this.activeDownloads.forEach((controller) => {
      controller.abort();
    });
    this.activeDownloads.clear();
  }

  async startDownload(payload: DownloadPayload): Promise<void> {
    if (!('showDirectoryPicker' in window)) {
      this.notificationService.error(
        'File System Access API is not supported in this browser. Please use Chrome, Edge, or another Chromium-based browser.',
        true,
      );
      return;
    }

    try {
      const directoryHandle: FileSystemDirectoryHandle = await _window.showDirectoryPicker({
        mode: 'readwrite',
      });

      const data: OperationFeedbackItemData = {
        status: OperationFeedbackItemDownloadStatus.Progress,
        title: payload.fileName || 'download',
        totalSize: payload.totalSize,
        start: Date.now().toString(),
        currentFile: '',
        payload,
        downloadedFiles: 0,
        downloadedSize: 0,
      };

      const existingIndex = this.downloads.findIndex(
        (item) => item.title === data.title,
      );
      if (existingIndex >= 0) {
        await this.cancelDownload(this.downloads[existingIndex].payload.id);
      }

      this.downloads.push(data);
      this.updateDownloads();

      await this.downloadData(data, directoryHandle);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.logger.error(`Failed to start download: ${error.message}`);
        throw error;
      }
    }
  }

  async cancelDownloads(): Promise<void> {
    const filesInProgress = this.downloads.filter(
      (item) => item.status !== OperationFeedbackItemDownloadStatus.Completed,
    );

    for (const download of filesInProgress) {
      const controller = this.activeDownloads.get(download.payload.id);
      if (controller) {
        controller.abort();
        this.activeDownloads.delete(download.payload.id);
      }
    }

    this.downloads = [];
    this.updateDownloads();
  }

  async retryDownload(payload: OperationFeedbackItemData): Promise<void> {
    if (!('showDirectoryPicker' in window)) {
      this.notificationService.error(
        'File System Access API is not supported in this browser. Please use Chrome, Edge, or another Chromium-based browser.',
        true,
      );
      return;
    }

    const existingIndex = this.downloads.findIndex(
      (item) => item.title === payload.title,
    );
    if (existingIndex >= 0) {
      this.downloads.splice(existingIndex, 1);
    }

    const data: OperationFeedbackItemData = {
      ...payload,
      status: OperationFeedbackItemDownloadStatus.Progress,
      start: Date.now().toString(),
      downloadedSize: 0,
      downloadedFiles: 0,
    };

    this.downloads.push(data);
    this.updateDownloads();

    try {
      const directoryHandle: FileSystemDirectoryHandle = await _window.showDirectoryPicker({
        mode: 'readwrite',
      });
      await this.downloadData(data, directoryHandle);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.logger.error(`Failed to retry download: ${error.message}`);
      }
    }
  }

  async cancelDownload(id: string): Promise<void> {
    const controller = this.activeDownloads.get(id);
    if (controller) {
      controller.abort();
      this.activeDownloads.delete(id);
    }

    this.downloads = this.downloads.filter(
      (download) => download.payload.id !== id,
    );
    this.updateDownloads();
  }

  private async downloadData(
    data: OperationFeedbackItemData,
    directoryHandle: FileSystemDirectoryHandle,
  ): Promise<void> {
    const abortController = new AbortController();
    this.activeDownloads.set(data.payload.id, abortController);

    try {
      data.downloadedFiles = 0;
      data.downloadedSize = 0;
      data.start = Date.now().toString();

      const blobThrottler = new Throttler(PARALLEL_DOWNLOADS);
      const activeBlobTasks = new Set<Promise<void>>();

      for await (const blob of this.iterBlobSession(
        data.payload.blob_session,
        data,
        abortController.signal,
      )) {
        if (abortController.signal.aborted) { break; }

        const task = blobThrottler.run(async () => {
          try {
            await this.processBlob(
              blob,
              directoryHandle,
              data,
              abortController.signal,
            );
          } finally {
            data.downloadedFiles = (data.downloadedFiles || 0) + 1;
            this.updateDownloads();
          }
        });

        activeBlobTasks.add(task);
        task.finally(() => activeBlobTasks.delete(task));

        if (activeBlobTasks.size >= PARALLEL_DOWNLOADS) {
          await Promise.race(activeBlobTasks);
        }
      }

      await Promise.all(activeBlobTasks);

      if (!abortController.signal.aborted) {
        data.status = OperationFeedbackItemDownloadStatus.Completed;
        this.updateDownloads();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        data.status = OperationFeedbackItemDownloadStatus.Failed;
      } else {
        data.status = OperationFeedbackItemDownloadStatus.Failed;
        this.logger.error(`Download failed: ${error.message}`);

        if (error.errors && Array.isArray(error.errors)) {
          const messages = error.errors.map(
            (err) => err.message || String(err),
          );
          this.logger.error(`Download failed: ${messages.join(', ')}`);
        }
      }
      this.updateDownloads();
    } finally {
      this.activeDownloads.delete(data.payload.id);
    }
  }

  private async *iterBlobSession(
    blobSessionId: string,
    data: OperationFeedbackItemData,
    signal: AbortSignal,
  ): AsyncGenerator<XferBlob> {
    let lastSeqNo: number | null = null;
    let prevSeqNo = 0;
    const afterId: { after_id?: string } = {};
    let bs = await this.getBlobSession(blobSessionId, signal);

    if (signal.aborted) { return; }

    const iterBlobs = async function* (
      this: DownloadService,
      lastSeqNo: number | null,
    ) {
      const params: PageQueryParams = {
        filter: [`blob_session_id=${blobSessionId}`],
        sort: lastSeqNo ? 'seq_no:asc' : 'path:asc',
        limit: 100,
      };
      if (lastSeqNo) { params.filter.push(`seq_no<=${lastSeqNo}`); }
      let total = 0;

      if (signal.aborted) { return; }

      let page = await this.getBlobsPage(
        Object.assign({}, params, afterId),
        signal,
      );

      let nextPagePromise: Promise<{ results: XferBlob[]; total: number }> | null = null;

      while (page.results.length > 0) {
        afterId.after_id = page.results[page.results.length - 1]._id;
        total += page.results.length;

        const hasMorePages = total < page.total;
        if (hasMorePages && !signal.aborted) {
          nextPagePromise = this.getBlobsPage(
            Object.assign({}, params, afterId),
            signal,
          );
        }

        for (const blob of page.results) {
          yield blob;
        }

        if (!hasMorePages) { break; }

        if (nextPagePromise) {
          page = await nextPagePromise;
          nextPagePromise = null;
        }

        if (signal.aborted) { break; }

        if (bs.open) {
          bs = await this.getBlobSession(blobSessionId, signal);
          if (!bs.open) {
            data.totalFiles = bs.files;
            data.totalSize = bs.bytes;
          }
        }
      }
    }.bind(this);

    while (bs.open) {
      lastSeqNo = bs.last_seq_no || 0;
      if (lastSeqNo === prevSeqNo) {
        await new Promise((resolve) =>
          setTimeout(resolve, SESSION_CHECK_DELAY_MS),
        );
        if (signal.aborted) { return; }
        bs = await this.getBlobSession(blobSessionId, signal);
        continue;
      }
      for await (const blob of iterBlobs(lastSeqNo)) {
        yield blob;
      }

      prevSeqNo = lastSeqNo;
      bs = await this.getBlobSession(blobSessionId, signal);
    }

    bs = await this.getBlobSession(blobSessionId, signal);
    lastSeqNo = bs.last_seq_no || 0;
    data.totalFiles = bs.files;
    data.totalSize = bs.bytes;

    if (!lastSeqNo || prevSeqNo < lastSeqNo) {
      for await (const blob of iterBlobs(lastSeqNo)) {
        yield blob;
      }
    }
  }

  private async getBlobSession(
    blobSession: string,
    signal: AbortSignal,
  ): Promise<XferBlobSession> {
    const abort$ = new Subject<void>();
    signal.addEventListener('abort', () => abort$.next());
    return await firstValueFrom(
      this.http.get<XferBlobSession>(`/xfer/blob-sessions/${blobSession}`).pipe(
        retry(exponentialBackoffRetry()),
        takeUntil(abort$),
      ),
    );
  }

  private async getBlobsPage(params: PageQueryParams, signal: AbortSignal): Promise<{ results: XferBlob[]; total: number }> {
    const abort$ = new Subject<void>();
    signal.addEventListener('abort', () => abort$.next());
    const searchParams = new URLSearchParams();
    params.filter.forEach(f => searchParams.append('filter', f));
    searchParams.set('sort', params.sort);
    searchParams.set('limit', params.limit.toString());
    if (params.after_id) {
      searchParams.set('after_id', params.after_id);
    }
    return await firstValueFrom(
      this.http.get<{ results: XferBlob[]; total: number }>(`/xfer/blobs?${searchParams}`).pipe(
        retry(exponentialBackoffRetry()),
        takeUntil(abort$),
      ),
    );
  }

  private async processBlob(
    blob: XferBlob,
    directoryHandle: FileSystemDirectoryHandle,
    data: OperationFeedbackItemData,
    signal: AbortSignal,
  ): Promise<void> {
    data.currentFile = blob.path;
    this.updateDownloads();
    const fileSize = blob.size;

    const pathParts = blob.path.split('/');
    let currentDir: FileSystemDirectoryHandle = directoryHandle;

    for (let i = 0; i < pathParts.length - 1; i++) {
      const dirName = pathParts[i];
      currentDir = await currentDir.getDirectoryHandle(dirName, {
        create: true,
      });
    }

    const fileName = pathParts[pathParts.length - 1];
    let existingFileHandle: FileSystemFileHandle | null = null;
    try {
      existingFileHandle = await currentDir.getFileHandle(fileName, {
        create: false,
      });
      const existingFile = await existingFileHandle.getFile();

      if (existingFile.size === fileSize) {
        data.downloadedSize = (data.downloadedSize || 0) + fileSize;
        this.updateDownloads();
        return;
      }
    } catch (e) {
      // non existing file, download normally
    }

    const abort$ = new Subject<void>();
    signal.addEventListener('abort', () => abort$.next());
    const download = await this.throttler.run(() =>
      firstValueFrom(
        this.http.post<{ download_url: string }>(`/xfer/blobs/${blob._id}/download`, {}).pipe(
          retry(exponentialBackoffRetry()),
          takeUntil(abort$),
        ),
      ),
    );

    const downloadUrl = this.prepareUrl(download.download_url);

    const fileHandle = await currentDir.getFileHandle(fileName, {
      create: true,
    });
    const writable = await fileHandle.createWritable();

    try {
      const downloadFullFile = async (): Promise<void> => {
        await this.throttler.run(async () => {
          const response = await fetchWithRetry(downloadUrl, { signal });
          const reader = response.body.getReader();
          try {
            let result = await reader.read();
            while (!result.done) {
              await writable.write(result.value);
              data.downloadedSize = (data.downloadedSize || 0) + result.value.byteLength;
              this.updateDownloads();
              result = await reader.read();
            }
          } finally {
            reader.releaseLock();
          }
        });
      };

      if (rangeReadUnsupported.test(downloadUrl)) {
        await downloadFullFile();
      } else {
        const ranges = [];
        for (let offset = 0; offset < fileSize; offset += CHUNK_SIZE) {
          ranges.push({
            start: offset,
            end: Math.min(offset + CHUNK_SIZE - 1, fileSize - 1),
          });
        }

        let rangeRequestFailed = false;
        for (let i = 0; i < ranges.length; i += PARALLEL_DOWNLOADS) {
          if (rangeRequestFailed) {
            // Fall back to full file download if range requests are not supported
            await downloadFullFile();
            break;
          }

          const currentRanges = ranges.slice(i, i + PARALLEL_DOWNLOADS);
          const chunkPromises = currentRanges.map((range, index) =>
            this.throttler.run(async () => {
              try {
                const response = await fetchWithRetry(downloadUrl, {
                  signal,
                  headers: { Range: `bytes=${range.start}-${range.end}` },
                });
                const chunkArrayBuffer = await response.arrayBuffer();
                return {
                  index: i + index,
                  data: chunkArrayBuffer,
                  size: chunkArrayBuffer.byteLength,
                };
              } catch (error) {
                if (error.status === 416) {
                  // Range not satisfiable - server doesn't support range requests
                  rangeRequestFailed = true;
                  return null;
                }
                throw error;
              }
            }),
          );

          const resolvedChunks = (await Promise.all(chunkPromises)).filter(
            Boolean,
          );

          if (rangeRequestFailed) {
            // Don't write partial chunks, will download full file instead
            continue;
          }

          resolvedChunks.sort((a, b) => a.index - b.index);

          for (const chunk of resolvedChunks) {
            await writable.write(chunk.data);
            data.downloadedSize = (data.downloadedSize || 0) + chunk.size;
          }

          this.updateDownloads();
        }
      }

      await writable.close();
    } catch (error) {
      await writable.abort();
      throw error;
    }
  }

  private prepareUrl(url: string): string {
    return url.replace(RegExp('^.*(/api/files.*)'), '$1');
  }

  private updateDownloads(): void {
    this.downloads$.next(JSON.parse(JSON.stringify(this.downloads)));
  }
}
